import { PRICE_MODE_STARTING_FROM, PRICE_MODE_FROM_TO, PRICE_MODE_DEFAULT } from '@bluheadless/ui-logic/src/constants'
import { useConfig } from '@bluheadless/ui-logic/src/providers/config'
import { useCurrency } from '@bluheadless/ui-logic/src/providers/currency'
import { useCurrencyFormatter } from '@bluheadless/ui-logic/src/hooks/useCurrencyFormatter'
import { any, arrayOf, bool, node, number, oneOf, oneOfType, shape, string } from 'prop-types'
import {
	DefaultPrice,
	DiscountPercentage,
	FromLabel,
	BasePrice,
	PriceWrapper,
	SpecialPrice,
	ToLabel,
	MiddlePrice,
	OldPrice,
} from './price.styled'
import { useIntl } from 'react-intl'

export const PriceRaw = ({
	className,
	amount,
	currency: currencyProp,
	currencyDisplay: currencyDisplayProp,
	currencyPosition: currencyPositionProp,
	customSymbol: customSymbolProp,
	hideSymbol,
	locale: localeProp,
	priceProps,
	useGrouping,
}) => {
	const {
		locale,
		displayCurrencies,
		price: { currencyPosition: currencyPositionContext, intlNumberFormatOptions },
	} = useConfig()
	const { currency: currencyContext } = useCurrency()

	const currentCurrency = currencyProp || currencyContext
	const currencyDisplay = currencyDisplayProp || displayCurrencies[currentCurrency]?.currencyDisplay
	const customSymbol = customSymbolProp || displayCurrencies[currentCurrency]?.customSymbol
	const currencyPosition = currencyPositionProp || currencyPositionContext || 'default'

	const formatter = useCurrencyFormatter(localeProp || locale, {
		currency: currentCurrency,
		currencyDisplay: currencyDisplay,
		useGrouping,
		...intlNumberFormatOptions,
	})

	let currencySymbol = ''
	let currencyPositionIndex = 0

	let price = formatter
		.formatToParts(amount)
		.map(({ type, value }, i) => {
			switch (type) {
				case 'currency':
					/**
					 * save the currency position index and remove the symbol to be able to
					 * modify it, if customSymbol is passed,
					 * and to be able to add it later to the right position.
					 */
					currencyPositionIndex = i
					currencySymbol = hideSymbol ? '' : customSymbol || value
					return ''
				case 'literal':
					// removes white space after currency symbol when using `currencyDisplay: 'code'`
					return ''
				default:
					return value
			}
		})
		.reduce((string, part) => string + part)

	price =
		(currencyPosition === 'default' && currencyPositionIndex === 0) || currencyPosition === 'left'
			? `${currencySymbol} ${price}`
			: `${price} ${currencySymbol}`

	return (
		<span className={className} {...priceProps}>
			{price}
		</span>
	)
}

const Price = ({
	amount,
	className,
	mode,
	showPercentage: showPercentageProp,
	specialAmount,
	startingFromLabel,
	fromToLabels,
	columnLayout,
	middlePrice,
	oldPrice,
	...props
}) => {
	const {
		price: { showPercentage: showPercentageContext },
	} = useConfig()
	const { formatMessage } = useIntl()

	const showPercentage = showPercentageProp ?? showPercentageContext

	const renderPercentage = (from, to) => {
		const priceDiff = from - to
		const discountValue = Math.round((priceDiff / from) * 100)
		const percentageLabel = `-${discountValue}%`
		return percentageLabel
	}

	/**
	 * @param {number} amount - the price amount
	 * @param {string} className - the className to be applied to the price wrapper
	 * @param {node} Wrapper - the component to be used as wrapper
	 * @returns {node} the price component wrapped in the Wrapper component
	 */
	const renderPrice = (amount, className = 'price', Wrapper = DefaultPrice) => (
		<Wrapper columnLayout={columnLayout} className={className}>
			<PriceRaw amount={amount} {...props} />
		</Wrapper>
	)

	return (
		// amount could be 0, but not null or undefined.
		amount !== null &&
		amount !== undefined && (
			<PriceWrapper className={className} columnLayout={columnLayout}>
				{mode === PRICE_MODE_STARTING_FROM ? (
					<>
						<FromLabel columnLayout={columnLayout}>
							{startingFromLabel || formatMessage({ id: 'price_starting_from' })}
						</FromLabel>

						{(oldPrice && oldPrice > amount) ||
						(middlePrice && middlePrice < amount) ||
						(specialAmount && specialAmount < amount) ? (
							<>
								{oldPrice && oldPrice > amount ? (
									<>
										{renderPrice(oldPrice, 'old-price', OldPrice)}

										{showPercentage && (
											<DiscountPercentage className="discount-percentage" columnLayout={columnLayout}>
												{renderPercentage(oldPrice, amount)}
											</DiscountPercentage>
										)}
									</>
								) : null}

								{renderPrice(amount, 'starting-from-price', BasePrice)}
								{middlePrice && middlePrice < amount ? (
									<>
										{showPercentage && (
											<DiscountPercentage className="discount-percentage" columnLayout={columnLayout}>
												{renderPercentage(amount, middlePrice)}
											</DiscountPercentage>
										)}
										{renderPrice(middlePrice, 'middle-price', MiddlePrice)}
									</>
								) : null}

								{specialAmount && specialAmount < amount ? (
									<>
										{showPercentage && (
											<DiscountPercentage className="discount-percentage" columnLayout={columnLayout}>
												{renderPercentage(middlePrice && middlePrice < amount ? middlePrice : amount, specialAmount)}
											</DiscountPercentage>
										)}
										{renderPrice(specialAmount, 'special-price', SpecialPrice)}
									</>
								) : null}
							</>
						) : (
							renderPrice(amount, 'starting-from-price')
						)}
					</>
				) : mode === PRICE_MODE_FROM_TO && amount < specialAmount ? (
					<>
						<FromLabel columnLayout={columnLayout}>
							{fromToLabels?.from ?? formatMessage({ id: 'price_from' })}
						</FromLabel>
						{renderPrice(amount, 'from-price')}
						<ToLabel columnLayout={columnLayout}>{fromToLabels?.to ?? formatMessage({ id: 'price_to' })}</ToLabel>
						{renderPrice(specialAmount, 'to-price')}
					</>
				) : (
					// default mode
					<>
						{(oldPrice && oldPrice > amount) ||
						(middlePrice && middlePrice < amount) ||
						(specialAmount && specialAmount < amount) ? (
							<>
								{oldPrice && oldPrice > amount ? (
									<>
										{renderPrice(oldPrice, 'old-price', OldPrice)}

										{showPercentage && (
											<DiscountPercentage className="discount-percentage" columnLayout={columnLayout}>
												{renderPercentage(oldPrice, amount)}
											</DiscountPercentage>
										)}
									</>
								) : null}

								{(middlePrice && middlePrice < amount) || (specialAmount && specialAmount < amount)
									? renderPrice(amount, 'base-price', BasePrice)
									: renderPrice(amount)}

								{middlePrice && middlePrice < amount ? (
									<>
										{showPercentage && (
											<DiscountPercentage className="discount-middle-percentage" columnLayout={columnLayout}>
												{renderPercentage(amount, middlePrice)}
											</DiscountPercentage>
										)}
										{renderPrice(middlePrice, 'middle-price', MiddlePrice)}
									</>
								) : null}

								{specialAmount && specialAmount < amount ? (
									<>
										{showPercentage && (
											<DiscountPercentage className="discount-percentage" columnLayout={columnLayout}>
												{renderPercentage(middlePrice && middlePrice < amount ? middlePrice : amount, specialAmount)}
											</DiscountPercentage>
										)}
										{renderPrice(specialAmount, 'special-price', SpecialPrice)}
									</>
								) : null}
							</>
						) : (
							renderPrice(amount)
						)}
					</>
				)}
			</PriceWrapper>
		)
	)
}

PriceRaw.defaultProps = {
	useGrouping: true,
	hideSymbol: false,
}

PriceRaw.propTypes = {
	/**
	 * Amount to be shown.
	 * If `null` or `undefined` the price will be hidden.
	 */
	amount: number,
	/**
	 * The currency to use in currency formatting.
	 * Possible values are the ISO 4217 currency codes, such as `USD` for the US dollar,
	 * `EUR` for the euro, or `CNY` for the Chinese RMB.
	 * @see [Current currency & funds code list](https://www.currency-iso.org/en/home/tables/table-a1.html).
	 */
	currency: string,
	/**
	 * How to display the currency in currency formatting.
	 * Possible values:
	 * - `symbol`: use a localized currency symbol such as `$`, this is the default value.
	 * - `narrowSymbol`: use a narrow format symbol ("$100" rather than "US$100").
	 * - `code`: use the ISO 4217 currency code, e.g. `USD`.
	 * - `name`: use a localized currency name, e.g. `US Dollar`.
	 */
	currencyDisplay: oneOfType([oneOf(['symbol', 'narrowSymbol', 'code', 'name']), string]),
	/**
	 * The position of the currency symbol to the price.
	 * Possible values:
	 * - `default`: the currency symbol is positioned to the left or to the right of the price,
	 * based on the currency and the locale. This is the default value.
	 * - `left`: the currency symbol is positioned to the left of the price.
	 * - `right`: the currency symbol is positioned to the right of the price.
	 */
	currencyPosition: oneOfType([oneOf(['default', 'left', 'right']), string]),
	/**
	 * The currency custom symbol to use in currency formatting.
	 * It'll overwrite the current currency symbol.
	 */
	customSymbol: string,
	/**
	 * Hide the currency symbol.
	 */
	hideSymbol: bool,
	/**
	 * A string with a [BCP 47 language tag](https://datahub.io/core/language-codes/r/3.html),
	 * or an array of such strings.
	 * For the general form and interpretation of the `locales` argument,
	 * see the [Intl](https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl#locale_identification_and_negotiation) page.
	 * Fallback to `en-US`.
	 */
	locale: oneOfType([string, arrayOf(string)]),
	/**
	 * Whether to use grouping separators, such as thousands separators or thousand/lakh/crore separators.
	 * Default is `true`.
	 */
	useGrouping: bool,
	/**
	 * Props to be passed to the underlying `<span>` element.
	 */
	priceProps: any,
}

Price.defaultProps = {
	...PriceRaw.defaultProps,
	mode: PRICE_MODE_DEFAULT,
	showPercentage: undefined,
	columnLayout: false,
}

Price.propTypes = {
	...PriceRaw.propTypes,
	/**
	 * Amount to be shown.
	 */
	amount: number,
	/**
	 * Whether to show the default price or the starting from price
	 * or the from - to price.
	 */
	mode: oneOf([PRICE_MODE_DEFAULT, PRICE_MODE_STARTING_FROM, PRICE_MODE_FROM_TO]),
	/**
	 * Enables percentage when special amount is `> 0`.
	 */
	showPercentage: bool,
	/**
	 * Special amount to be shown.
	 */
	specialAmount: number,
	/**
	 * Label to be shown when mode is `starting-from`.
	 */
	startingFromLabel: node,
	/**
	 * Labels to be shown when mode is `from-to`.
	 */
	fromToLabels: shape({
		from: node,
		to: node,
	}),
	/**
	 * Display the price in column.
	 */
	columnLayout: bool,
}

export default Price
